/**
 * Recipe Ingredient Quantity & Portion Scaler
 * Scales quantities dynamically based on portion count while preserving units, fractions, and text notes.
 */

function scaleSingleNumeric(rawNumPart: string, factor: number): string | null {
  // Helper to parse a single number or fraction
  const parseNumOrFraction = (str: string): number | null => {
    str = str.trim().replace(',', '.')
    if (!str) return null
    if (str.includes('/')) {
      const parts = str.split('/')
      if (parts.length === 2) {
        const num = parseFloat(parts[0])
        const den = parseFloat(parts[1])
        if (!isNaN(num) && !isNaN(den) && den !== 0) {
          return num / den
        }
      }
      return null
    }
    const val = parseFloat(str)
    return isNaN(val) ? null : val
  }

  // Handle compound fractions like "1 1/2"
  let parsedValue: number | null = null
  const spaceParts = rawNumPart.split(/\s+/)
  if (spaceParts.length === 2 && spaceParts[1].includes('/')) {
    const whole = parseFloat(spaceParts[0].replace(',', '.'))
    const frac = parseNumOrFraction(spaceParts[1])
    if (!isNaN(whole) && frac !== null) {
      parsedValue = whole + frac
    }
  } else {
    parsedValue = parseNumOrFraction(rawNumPart)
  }

  if (parsedValue === null) return null

  const scaled = parsedValue * factor

  // Format scaled value nicely into whole numbers, fractions or rounded decimals
  const formatScaledNumber = (n: number): string => {
    if (Math.abs(n - Math.round(n)) < 0.01) {
      return String(Math.round(n))
    }
    const intPart = Math.floor(n)
    const fracPart = n - intPart
    if (Math.abs(fracPart - 0.5) < 0.02) {
      return intPart > 0 ? `${intPart} 1/2` : '1/2'
    }
    if (Math.abs(fracPart - 0.25) < 0.02) {
      return intPart > 0 ? `${intPart} 1/4` : '1/4'
    }
    if (Math.abs(fracPart - 0.75) < 0.02) {
      return intPart > 0 ? `${intPart} 3/4` : '3/4'
    }
    if (Math.abs(fracPart - 0.33) < 0.03) {
      return intPart > 0 ? `${intPart} 1/3` : '1/3'
    }
    if (Math.abs(fracPart - 0.67) < 0.03) {
      return intPart > 0 ? `${intPart} 2/3` : '2/3'
    }

    // Default to at most 2 decimal places with comma
    return parseFloat(n.toFixed(2)).toString().replace('.', ',')
  }

  return formatScaledNumber(scaled)
}

export function scaleAmount(amount: string | undefined | null, baseServings: number, targetServings: number): string {
  if (!amount || typeof amount !== 'string') return ''
  if (!baseServings || baseServings <= 0 || !targetServings || targetServings <= 0 || baseServings === targetServings) {
    return amount.trim()
  }

  const factor = targetServings / baseServings
  const trimmed = amount.trim()

  // Check for range e.g. "10 - 12 g", "10-12", "1 1/2 - 2 EL"
  const rangeMatch = trimmed.match(/^([\d.,/]+(?:\s+[\d.,/]+)?)\s*[-–—]\s*([\d.,/]+(?:\s+[\d.,/]+)?)(.*)$/)
  if (rangeMatch) {
    const minPart = rangeMatch[1].trim()
    const maxPart = rangeMatch[2].trim()
    const textPart = rangeMatch[3].trim()

    const scaledMin = scaleSingleNumeric(minPart, factor)
    const scaledMax = scaleSingleNumeric(maxPart, factor)

    if (scaledMin && scaledMax) {
      const rangeStr = `${scaledMin} - ${scaledMax}`
      return textPart ? `${rangeStr} ${textPart}`.trim() : rangeStr
    }
  }

  // Single quantity (including compound fraction "1 1/2") and trailing unit/text
  const match = trimmed.match(/^([\d.,/]+(?:\s+[\d.,/]+)?)(.*)$/)
  if (!match) return trimmed

  const rawNumPart = match[1].trim()
  const textPart = match[2].trim()

  const scaledNum = scaleSingleNumeric(rawNumPart, factor)
  if (scaledNum === null) return trimmed

  if (!textPart) return scaledNum
  return `${scaledNum} ${textPart}`.trim()
}
